import * as React from 'react'
import { useRef } from 'react'
import { classes, media, style as _style } from 'typestyle'

/* Hooks ======================================================================================== */
import useCounter from '../../hooks/use-counter'

/* Components =================================================================================== */
import { Selector } from '../selector/selector.component'
import { Box } from '../box/box.component'
import { Text } from '../text/text.component'

/* Styles ======================================================================================= */
import { tabsClass } from './tabs.class'
import { mr8 } from '../../styles/styleset/margin/mr8'
import { fitHeight } from '../../styles/styleset/fit-height/fitHeight'
import { w100 } from '../../styles/styleset/width/w100'
import { p12 } from '../../styles/styleset/padding/p12'
import { mr4 } from '../../styles/styleset/margin/mr4'
import { mb8 } from '../../styles/styleset/margin/mb8'
import { paddingLeftInner } from '../../styles/default/padding'

/* Types ======================================================================================== */
type TabsProps = {
  style?: React.CSSProperties
  children?: any
  className?: string
  onSuccess?: () => void
  onMove?: (step: number, current: number, traverse: (step: number) => void) => void
  allowIndex?: boolean
  topOffset?: any
  footer?: React.ReactNode
}

/* <Tabs /> ===================================================================================== */
export const Tabs: React.FC<TabsProps> = ({
  children,
  allowIndex = false,
  onMove,
  className,
  style,
  topOffset = 0,
  footer,
}) => {
  if (!Array.isArray(children)) {
    children = [children]
  }
  const [stepCount, $stepCount] = useCounter(children.findIndex((a: any) => !!a.props?.label))

  const traverseStep = (step: any) => $stepCount.set(step)
  const headerRef = useRef()
  const headerCompRef = useRef()

  const move = (step: any) => () => {
    if (onMove) {
      onMove(step, stepCount, traverseStep)
    } else {
      traverseStep(step)
    }
  }

  const renderHeader = () => (
    <Box
      baseline="surface"
      ref={headerCompRef}
      className={classes(
        w100,
        // fitHeight,
        _style({
          borderRadius: '4px 4px 0 0',
          overflowX: 'auto',
          whiteSpace: 'nowrap',
          $nest: {
            // '&::-webkit-scrollbar': {
            //   display: 'none',
            // },
          },
        }),
      )}
    >
      <Selector
        selected={[...children].findIndex((a: any) => !!a.props?.label)}
        spacing={16}
        parentRef={headerCompRef}
      >
        {(() => {
          if (children) {
            if (!children.length && children.props) {
              return (
                <Box baseline="surface">
                  <Box baseline="surface" className={classes(p12)} onClick={move(0)} alignCenterY>
                    {allowIndex && (
                      <Text size="xs" className={mr4} semiBold>
                        1
                      </Text>
                    )}
                    {children.props.label && (
                      <Text size="xs" disableMediaQuery>
                        {children.props.label}
                      </Text>
                    )}
                  </Box>
                </Box>
              )
            }

            return [...children].map((item: any, key: number) => {
              if (!item || item.props.hidden) {
                return <></>
              }

              let borderRadius = '0px 0px 0px 0px !important'

              if (key === 0) {
                borderRadius = '4px 0px 0px 0px !important'
              } else if (key === children.length - 1) {
                borderRadius = '0px 4px 0px 0px !important'
              }

              return (
                <Box baseline="surface" key={key} className={fitHeight} rounded>
                  <Box
                    baseline="none"
                    className={classes(
                      paddingLeftInner,
                      _style({
                        borderRadius,
                        height: 48,
                        background: item.props.highlight ? '#fff0c7' : '',
                      }),
                    )}
                    onClick={move(key)}
                    alignCenterY
                  >
                    {item.props.hasError && (
                      <i
                        className={classes(
                          'material-icons',
                          _style({ fontSize: '16px !important', color: '#ffa300', marginRight: 4 }),
                        )}
                      >
                        warning
                      </i>
                    )}
                    {allowIndex && (
                      <Text
                        size="xs"
                        className={classes(mr8, mb8)}
                        shrink
                        semiBold
                        baseline="surface"
                      >
                        {key + 1}
                      </Text>
                    )}
                    {item.props.label && (
                      <Text
                        size="sm"
                        disableMediaQuery
                        light={stepCount !== key}
                        semiBold={stepCount === key}
                        baseline="surface"
                      >
                        {item.props.label}
                      </Text>
                    )}
                  </Box>
                </Box>
              )
            })
          }
        })()}
      </Selector>
    </Box>
  )

  const { base, header } = tabsClass.setProps({})

  return (
    <div className={classes('aeki-tabs', base, className)} style={style}>
      <Box
        baseline="none"
        ref={headerRef}
        className={classes(header, _style({ ...media({ type: 'print' }, { display: 'none' }) }))}
        style={{ top: topOffset }}
        sticky
      >
        {renderHeader()}
      </Box>

      <Box baseline="surface" rounded fullHeight>
        {(() => {
          if (children) {
            if (!children.length) {
              return React.cloneElement(children, {
                ...children.prop,
                currentStep: stepCount,
                step: 0,
                key: 0,
              })
            }
            return children.map((child: any, index: number) => {
              if (!child) return

              return React.cloneElement(child, {
                ...child.prop,
                currentStep: stepCount,
                step: index,
                key: index,
              })
            })
          }
        })()}
      </Box>
      {footer}
    </div>
  )
}

/* Export ======================================================================================= */
export default Tabs
